import { useEffect } from 'react';
import { useLocalization } from '../../../../../context/Localization/localization';
import { useSetHeaderProps, useSetKioskBannerVisibility } from '../../../../../context/Page/page';
import { CTAButton } from '../../../../../controls/ButtonBase';
import { Heading } from '../../../../../controls/Heading/Heading';
import { ScrollableBody } from '../../../../Layout/ScrollableBody/ScrollableBody';
import { GMButtonGroup } from '../../../../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../../../../controls/Icon';
import { GoMotoComponent } from '../../../../../API/GoMoto.types';
import { DoubleCheckLockerProps } from './DoubleCheckLocker.types';
import { LocalizationType } from '../../../../../context/Localization/localization.types';
import { AppointmentLoanerOrRentalOption } from '../../../../../API/Enums';

// TODO: Delete this component since the `GMDoubleCheckLocker` control already integrates everything needed here. First need to replace it's use though.

/**
 * Asks the user to make sure that they correctly retrieved their key or exchanged keys at the locker.
 * They can re-open the locker if they need to, otherwise they can confirm they correctly finished picking up
 * their vehicle.
 *
 * @param onReopenLocker - {@link DoubleCheckLockerProps.onReopenLocker onReopenLocker} description.
 * @param onContinue - {@link DoubleCheckLockerProps.onContinue onContinue} description.
 * @param appointment - {@link DoubleCheckLockerProps.appointment appointment} description.
 */
export const DoubleCheckLocker: GoMotoComponent<DoubleCheckLockerProps> = ({
    onReopenLocker,
    onContinue,
    appointment,
}) => {
    const Localization: LocalizationType = useLocalization();
    const showBanner = useSetKioskBannerVisibility();
    const setHeader = useSetHeaderProps();
    const isKeyExchange: boolean = appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ||
        appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Rental;

    useEffect(() => {
        const timer = setTimeout(() => {
            onContinue();
        }, 60000);

        return () => {
            clearTimeout(timer);
        };
    }, [onContinue]);

    useEffect(() => {
        showBanner(true);
        setHeader(undefined);
    }, [setHeader, showBanner]);

    return (
        <ScrollableBody
            heading={
                <Heading
                    primaryHeading={Localization.DoubleChecking}
                    secondaryHeading={isKeyExchange ? (
                        Localization.formatString(
                            Localization.DidYouReturnTheKey,
                            appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ? (
                                <b>{Localization.Loaner.toLocaleLowerCase()}</b>
                            ) : (
                                <b>{Localization.Rental.toLocaleLowerCase()}</b>
                            ),
                        )
                    ) : (
                        Localization.DidYouRetrieveYourKeys
                    )}
                    roundedIcon={
                        <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                            <GMSizedIcon iconType={'Key'} iconSize={'extraLarge'} />
                        </GMRoundedIcon>
                    }
                    showMustache
                />
            }
            proceedButtons={
                <GMButtonGroup
                    alignment={'center'}
                    buttons={[
                        (
                            <CTAButton
                                styleType={'primary'}
                                label={Localization.YesContinue}
                                onClick={onContinue}
                                kioskOptions={{
                                    square: true,
                                }}
                                id={Localization.YesContinue}
                                ariaLabel={Localization.YesContinue}
                            />
                        ),
                        (
                            <CTAButton
                                styleType={'secondary'}
                                onClick={onReopenLocker}
                                label={Localization.NoReopenLocker}
                                kioskOptions={{
                                    square: true,
                                }}
                                id={Localization.NoReopenLocker}
                                ariaLabel={Localization.NoReopenLocker}
                            />
                        ),
                    ]}
                />
            }
            unsetButtons
        />
    );
};
